import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, readErr } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import KundaliChart from '@/components/KundaliChart';
import TimingWindows from '@/components/TimingWindows';
import MatchResultCard, { MatchVerdictChip } from '@/components/MatchResultCard';
import RectifyBirthTimeModal from '@/components/RectifyBirthTimeModal';
import { useAuth } from '@/lib/auth';
import { toast } from '@/lib/toast';
import { ArrowLeft, Stars, Sparkles, Quote, Hourglass, Ban, AlertCircle, Lock, Coins, Gem, UserRound, Info, Compass } from 'lucide-react';

const VERDICT_STYLE = {
    YES: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    NO: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
    YES_WITH_DELAY: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
    NO_BUT_LATER: 'bg-sky-500/10 text-sky-400 border-sky-500/30',
    NOT_IN_WINDOW: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
};

const VERDICT_LABEL = {
    NOT_IN_WINDOW: 'Not in this window',
};

// B1-CHANGE 2 — strip markdown bold/italic markers from LLM-generated
// narration. The reading card renders plain text — markdown syntax
// appears as literal asterisks if not stripped. Display-only fix; the
// raw narration is preserved in the database unchanged.
const stripMarkdownBold = (text) => {
    if (typeof text !== 'string') return text;
    return text
        .replace(/\*\*(.+?)\*\*/g, '$1')   // **bold**
        .replace(/(^|[^*])\*(?!\s)([^*\n]+?)\*(?!\*)/g, '$1$2');  // *italic* (avoid eating ** twice)
};

// Round 16 — narration progress bar. Replaces the static skeleton with a
// realistic 0→100 progress animation + rotating status messages. The bar
// is driven by the backend's `progress` field (0-99 while pending, 100
// when narration completes — 0-90 linear over the median LLM time, then
// a slow 90→99 tick during the long tail so the bar never feels stuck).
function NarrationLoader({ progress, complete, text }) {
    let stage = '';
    let longTail = false;
    if (!complete) {
        if (progress <= 20) stage = 'Casting your chart…';
        else if (progress <= 40) stage = 'Analysing planetary positions…';
        else if (progress <= 60) stage = 'Computing significators and ruling planets…';
        else if (progress <= 80) stage = 'Consulting the KP framework…';
        else if (progress <= 90) stage = 'Composing your reading…';
        else {
            stage = 'Weaving the final lines…';
            longTail = true;
        }
    }
    // When complete the bar animates to 100% then fades out (parent
    // unmounts after 600ms via the `narrationDone` flag).
    const widthPct = complete ? 100 : Math.max(2, Math.min(99, progress));
    return (
        <div
            data-testid="narration-loader"
            data-progress={progress}
            className={`transition-opacity duration-300 ${complete ? 'opacity-0' : 'opacity-100'}`}
        >
            <div className="h-1 w-full bg-marigold/10 rounded-full overflow-hidden">
                <div
                    data-testid="narration-progress-bar"
                    className="h-full bg-marigold rounded-full"
                    style={{
                        width: `${widthPct}%`,
                        transition: 'width 1.2s ease-in-out',
                    }}
                />
            </div>
            <div
                data-testid="narration-status-message"
                className="mt-3 flex items-center justify-center gap-2 text-xs text-slate-500 tracking-wide min-h-[1.25rem]"
            >
                {longTail && (
                    <span
                        data-testid="narration-pulse"
                        className="inline-block w-1.5 h-1.5 rounded-full bg-marigold animate-pulse"
                    />
                )}
                <span>{stage}</span>
            </div>
        </div>
    );
}

export default function PredictionDetail() {
    const { id } = useParams();
    const [q, setQ] = useState(null);
    const [chartStyle, setChartStyle] = useState('north');
    const [rectifyOpen, setRectifyOpen] = useState(false);
    // Round 16 — narration progress bar state
    const [narrationDone, setNarrationDone] = useState(false);  // true after fade-out completes
    const [progress, setProgress] = useState(0);

    // Round 16 — Win 1: when /ask returns the deterministic verdict
    // immediately, the LLM narration is still generating in the
    // background. Poll until `narration_pending` flips to false so the
    // reading body fills in without a hard refresh.
    useEffect(() => {
        let cancelled = false;
        let timer;
        // Track whether we ever observed narration_pending=true. When the
        // very first GET already has narration_pending=false (visiting an
        // older reading from history), skip the bar→fade transition.
        let everPending = false;

        const tick = async () => {
            try {
                const { data } = await api.get(`/astrology/prediction/${id}`);
                if (cancelled) return;
                setQ(data);
                if (typeof data.progress === 'number') {
                    setProgress(data.progress);
                }
                if (data.narration_pending) {
                    everPending = true;
                    timer = setTimeout(tick, 1500);
                } else if (!everPending) {
                    // First GET already had a completed narration —
                    // render text immediately, no transition.
                    setNarrationDone(true);
                } else {
                    // Round 16 — completion transition. Animate progress
                    // to 100, hold for 300ms, then unmount the loader so
                    // the narration text fades in.
                    setProgress(100);
                    setTimeout(() => !cancelled && setNarrationDone(true), 600);
                }
            } catch (e) {
                if (!cancelled) timer = setTimeout(tick, 3000);
            }
        };
        tick();

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [id]);

    if (!q) return <div className="max-w-4xl mx-auto px-6 py-16 text-slate-500">Loading reading…</div>;

    // B1-CHANGE 1 — Not-configured (longevity) — render a warm
    // consultant card instead of the standard reading layout. No chart,
    // no chip, no remedies — just the explanation and a CTA to book a
    // human astrologer.
    // A101 — Prenatal-gender PCPNDT block renders a distinct compliance
    // card: title acknowledges legal context, body affirms what we CAN
    // share, no consultant CTA (we cannot route to a human for the
    // gender either — the law applies to every channel).
    if (q.is_not_configured || q.verdict === 'NOT_CONFIGURED') {
        const isPcpndt = q.is_prenatal_gender_block || q.not_configured_reason === 'prenatal_gender_pcpndt';
        const title = isPcpndt
            ? 'Predictions about an unborn child\'s gender are not provided'
            : 'This question needs a human astrologer';
        const overline = isPcpndt
            ? 'Legal & ethical guideline (PCPNDT Act, 1994)'
            : 'Human astrologer needed';
        return (
            <div className="max-w-3xl mx-auto px-6 py-12" data-testid="not-configured-page">
                <Link to="/app/history" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-marigold mb-8">
                    <ArrowLeft size={14} /> Back to history
                </Link>
                <Card
                    data-testid={isPcpndt ? 'pcpndt-card' : 'not-configured-card'}
                    className="bg-elevated border-marigold/30 rounded-sm p-10"
                >
                    <div className="overline mb-4 flex items-center gap-2 text-marigold">
                        <UserRound size={14} /> {overline}
                    </div>
                    <h2 className="font-serif text-2xl text-parchment mb-4 leading-snug">
                        {title}
                    </h2>
                    <p className="text-slate-300 leading-relaxed mb-6">
                        {q.not_configured_message || q.prediction_text}
                    </p>
                    {q.not_configured_suggestion && (
                        <p className="text-sm text-slate-400 mb-8">
                            {q.not_configured_suggestion}
                        </p>
                    )}
                    {!isPcpndt && (
                        <Link to="/app/consult">
                            <Button
                                data-testid="not-configured-cta"
                                className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-none h-11 px-6 font-medium"
                            >
                                <UserRound size={16} className="mr-2" /> Book Consultation
                            </Button>
                        </Link>
                    )}
                </Card>
            </div>
        );
    }

    // Round 16 — full-screen loader. While narration is being generated,
    // hide the chart / verdict chip / chart / fructification etc. and
    // show only the progress bar. This avoids overwhelming the user with
    // a half-finished reading; the bar is the only thing they care about
    // until the text appears.
    if (q.narration_pending || !narrationDone) {
        return (
            <div
                data-testid="narration-loading-screen"
                className="max-w-5xl mx-auto px-6 min-h-[calc(100vh-8rem)] flex flex-col items-center justify-center"
            >
                <div className="w-full max-w-md">
                    <div className="overline mb-6 flex items-center justify-center gap-2 text-marigold/80">
                        <Sparkles size={12} /> Composing your reading
                    </div>
                    <NarrationLoader
                        progress={progress}
                        complete={!q.narration_pending}
                        text={q.prediction_text}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto px-6 py-10 md:py-14" data-testid="prediction-page">
            <Link to="/app/history" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-marigold mb-8">
                <ArrowLeft size={14} /> Back to history
            </Link>

            {/* Round 16 — staggered fade-in cascade. Verdict chip lands first
              * (0ms), reading text second (150ms), then everything else (300ms).
              * Creates a "the chart is revealing itself" feel that matches the
              * mystical brand without delaying interactivity (all sections are
              * already mounted; only opacity animates). */}
            <div
                data-testid="cascade-1-header"
                className="mb-10 animate-in fade-in duration-500 fill-mode-both"
                style={{ animationDelay: '0ms' }}
            >
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    {q.is_match_prediction ? (
                        <MatchVerdictChip q={q} />
                    ) : q.not_in_window ? (
                        <Badge className={`rounded-sm border ${VERDICT_STYLE.NOT_IN_WINDOW}`} data-testid="verdict-badge">
                            Not in this window
                        </Badge>
                    ) : (
                        <Badge className={`rounded-sm border ${VERDICT_STYLE[q.verdict] || ''}`} data-testid="verdict-badge">
                            {q.verdict?.replace(/_/g, ' ')}
                        </Badge>
                    )}
                    <span className="overline">· {q.category} ·</span>
                    <span className="text-xs text-slate-500 uppercase tracking-wider">{q.prediction_mode}{q.horary_number ? ` · #${q.horary_number}` : ''}{q.ayanamsa ? ` · ${q.ayanamsa === 'kp' ? 'KP' : 'Lahiri'} ayanamsa` : ''}</span>
                    {/* A89 — Retrospective Reading pill. Appears only when
                        the engine ran a backward-looking trajectory
                        analysis ("past 50 years", "last 20 years",
                        "looking back over my childhood" etc.). */}
                    {q.direction === 'past' && (
                        <span
                            className="text-[10px] uppercase tracking-wider
                                       bg-marigold/10 text-marigold border border-marigold/30
                                       rounded-sm px-2 py-0.5"
                            data-testid="retrospective-pill"
                        >
                            Retrospective reading · Looking backward
                        </span>
                    )}
                </div>
                <h1 className="font-serif text-3xl md:text-4xl text-parchment leading-tight">
                    <Quote size={16} className="inline-block mr-2 text-copper" />{q.text}
                </h1>
                {q.not_in_window && (
                    <Card className="rounded-sm bg-amber-500/5 border border-amber-500/30 p-5 mt-5" data-testid="not-in-window-banner">
                        <div className="flex items-start gap-3">
                            <AlertCircle size={18} className="text-amber-400 mt-0.5 flex-shrink-0" />
                            <div className="text-sm leading-relaxed text-parchment">
                                <span className="text-amber-300 font-medium">This event does not appear to materialise within the timeframe you asked about.</span>
                                <span className="text-slate-400">
                                    {' '}The chart's longer arc supports the outcome, but the planetary chain (Maha–Antar–Pratyantar) does not align with the favourable significators or Ruling Planets within your stated window. The verdict label has been updated to reflect this.
                                </span>
                            </div>
                        </div>
                    </Card>
                )}
                <div className="text-xs text-slate-600 mt-3 space-y-1">
                    <div>Asked at {new Date(q.question_time_utc).toLocaleString()}</div>
                    {q.prediction_mode === 'natal' ? (
                        <div data-testid="chart-cast-line">
                            Reading based on your <span className="text-parchment">birth chart</span>
                            {q.chart_cast_at && (
                                <> · computed for {new Date(q.chart_cast_at).toLocaleString()}</>
                            )}
                            {q.chart_cast_location && <> · {q.chart_cast_location}</>}
                        </div>
                    ) : (
                        <div data-testid="chart-cast-line">
                            Horary chart cast at the moment of question
                            {q.chart_cast_location && <> · {q.chart_cast_location}</>}
                            {q.horary_number && <> · sub-lord point #{q.horary_number}</>}
                        </div>
                    )}
                </div>
            </div>

            {/* Round 17 — Compound-question banner. When the question
              * contained multiple sub-questions, surface the credit count
              * and sub-question list so the user understands why their
              * balance went down by N×10 instead of 10. */}
            {q.is_compound_question && q.sub_question_count > 1 && (
                <Card
                    data-testid="compound-question-banner"
                    className="bg-marigold/5 border-marigold/30 rounded-sm p-5 mb-6 animate-in fade-in duration-500 fill-mode-both"
                    style={{ animationDelay: '100ms' }}
                >
                    <div className="flex items-start gap-3">
                        <Sparkles size={14} className="text-marigold mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                            <div className="text-sm text-parchment font-medium mb-1">
                                Multi-part question · {q.sub_question_count} sub-questions
                            </div>
                            <div className="text-xs text-slate-400 leading-relaxed">
                                Charged <span className="text-marigold font-medium">{q.credits_charged ?? q.sub_question_count * 10} credits</span>
                                {' '}({(q.credits_charged ?? q.sub_question_count * 10) / q.sub_question_count} per part). The reading addresses each sub-question in turn.
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            <Card
                className="bg-elevated border-marigold/15 rounded-sm p-8 mb-10 animate-in fade-in duration-500 fill-mode-both"
                style={{ animationDelay: '150ms' }}
                data-testid="prediction-text"
            >
                <div className="overline mb-4 flex items-center gap-2">
                    <Sparkles size={12} /> Reading
                </div>
                {q.prediction_mode === 'natal' && (() => {
                    const btc = q.birth_time_confidence || 'not_set';
                    const isVerified = btc === 'exact';
                    const profileBit = q.profile_name ? <> for <span className="text-sky-200/90">{q.profile_name}</span></> : null;
                    return isVerified ? (
                        // Soft variant — user has marked their birth time as
                        // exact. We acknowledge that without nudging them to
                        // rectify (no CTA, lower visual weight).
                        <div
                            className="mb-6 rounded-sm border border-sky-500/15 bg-sky-500/[0.04] px-5 py-3 flex items-center gap-3"
                            data-testid="natal-birth-time-disclaimer"
                            data-variant="verified"
                        >
                            <Info size={16} className="text-sky-400/70 shrink-0" />
                            <div className="flex-1 text-[13px] leading-relaxed text-slate-400 font-sans">
                                Reading drawn from your <span className="text-sky-200/85">verified birth chart</span>{profileBit}.
                            </div>
                        </div>
                    ) : (
                        // Stronger variant — birth time confidence is
                        // approximate / confirmed / rectified / not_set.
                        // The full nudge to rectify is shown.
                        <div
                            className="mb-6 rounded-sm border border-sky-500/25 bg-sky-500/[0.06] px-5 py-4 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4"
                            data-testid="natal-birth-time-disclaimer"
                            data-variant="unverified"
                        >
                            <Info size={18} className="text-sky-400/90 mt-[2px] shrink-0" />
                            <div className="flex-1 text-[13.5px] leading-relaxed text-slate-300/95 font-sans">
                                This reading is drawn from your <span className="text-sky-200/90">birth chart</span>{profileBit}, so its accuracy depends on the correctness of the birth time on file.
                                {' '}If you are unsure about the exact minute, our <span className="text-sky-200/90">Birth Time Rectification</span> tool can evaluate it against known life events.
                            </div>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setRectifyOpen(true)}
                                className="shrink-0 border-sky-400/40 text-sky-200 hover:text-sky-100 hover:bg-sky-500/10 hover:border-sky-400/70 gap-1.5"
                                data-testid="natal-rectify-cta"
                            >
                                <Compass size={14} /> Rectify birth time
                            </Button>
                        </div>
                    );
                })()}
                <div className="prose prose-invert max-w-none font-serif text-lg text-parchment leading-relaxed whitespace-pre-wrap">
                    {stripMarkdownBold(q.prediction_text)}
                </div>
            </Card>

            <RectifyBirthTimeModal
                open={rectifyOpen}
                onOpenChange={setRectifyOpen}
                targetProfile={q.profile_id ? {
                    id: q.profile_id,
                    name: q.profile_name,
                    relation: q.profile_relation,
                    birth_details: q.birth_details || null,
                } : null}
                onSuccess={() => { setRectifyOpen(false); }}
            />

            {/* Cascade tier 3 — everything below the reading body fades in
              * together at 300ms. */}
            <div
                data-testid="cascade-3-extras"
                className="animate-in fade-in duration-500 fill-mode-both"
                style={{ animationDelay: '300ms' }}
            >

            {!q.suppress_remedies && (
                <RemediesCard
                    question={q}
                    onUnlocked={(updated) => setQ((prev) => ({ ...prev, ...updated }))}
                />
            )}

            {q.prediction_mode === 'horary' && q.is_match_prediction && (
                <section className="mb-6" data-testid="match-result-section">
                    <h2 className="font-serif text-2xl text-parchment mb-4">Match outcome</h2>
                    <MatchResultCard q={q} />
                </section>
            )}

            {q.prediction_mode === 'horary' && !q.is_match_prediction && q.fructification && (
                <section className="mb-6" data-testid="fructification-section">
                    <h2 className="font-serif text-2xl text-parchment mb-4">
                        Likely fructification window
                        {q.fructification?.horizon_days != null && (
                            <span className="ml-2 text-xs text-marigold uppercase tracking-widest font-sans align-middle">
                                · constrained to your asked timeframe
                            </span>
                        )}
                    </h2>
                    <FructificationCard f={q.fructification} />
                </section>
            )}

            <section className="mb-10 flex flex-col items-center text-center" data-testid="seek-astrologer-cta">
                <p className="text-sm text-slate-400 max-w-xl mb-4 leading-relaxed">
                    Want to talk this through with a human astrologer? Some questions deserve a human voice.
                </p>
                <Link to="/app/consult">
                    <Button
                        data-testid="seek-astrologer-btn"
                        className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-none h-11 px-6 font-medium"
                    >
                        <UserRound size={16} className="mr-2" /> Seek Astrologer Consultation
                    </Button>
                </Link>
            </section>

            <section className="grid lg:grid-cols-2 gap-6 mb-10">
                <Card className="bg-surface border-marigold/15 rounded-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="overline flex items-center gap-2"><Stars size={12} /> Kundali</div>
                        <Tabs value={chartStyle} onValueChange={setChartStyle} data-testid="chart-style-tabs">
                            <TabsList className="bg-obsidian border border-marigold/10 rounded-sm h-8">
                                <TabsTrigger value="north" data-testid="tab-north" className="text-xs data-[state=active]:bg-marigold data-[state=active]:text-obsidian rounded-none">
                                    North Indian
                                </TabsTrigger>
                                <TabsTrigger value="south" data-testid="tab-south" className="text-xs data-[state=active]:bg-marigold data-[state=active]:text-obsidian rounded-none">
                                    South Indian
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                    </div>
                    <KundaliChart chart={q.chart} style={chartStyle} size={380} />
                    <div className="mt-6 text-xs text-slate-500 text-center">
                        Ascendant: <span className="text-parchment">{q.chart.ascendant.sign} {q.chart.ascendant.degree.toFixed(2)}°</span>
                        {' · '}Sub-lord: <span className="text-parchment">{q.chart.ascendant.sub_lord}</span>
                    </div>
                </Card>

                <Card className="bg-surface border-marigold/15 rounded-sm p-6">
                    <div className="overline mb-4">Current Dasha</div>
                    <div className="space-y-4 text-sm">
                        {['maha', 'antar', 'pratyantar', 'sookshma'].map((k) => {
                            const d = q.dasha?.[k];
                            if (!d) return null;
                            const label = { maha: 'Mahadasha', antar: 'Antardasha', pratyantar: 'Pratyantardasha', sookshma: 'Sookshma' }[k];
                            return (
                                <div key={k} className="flex items-start justify-between gap-4 border-b border-marigold/5 pb-3 last:border-0 last:pb-0" data-testid={`dasha-${k}`}>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
                                        <div className="font-serif text-xl text-parchment">{d.lord}</div>
                                    </div>
                                    <div className="text-right text-xs text-slate-500">
                                        <div>{new Date(d.start).toLocaleDateString()}</div>
                                        <div>→ {new Date(d.end).toLocaleDateString()}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>
            </section>

            {q.prediction_mode === 'horary' && q.horary_tables && (
                <section className="mb-10" data-testid="horary-house-table">
                    <h2 className="font-serif text-2xl text-parchment mb-4">House table</h2>
                    <HouseTable rows={q.horary_tables.houses} />
                </section>
            )}

            {q.prediction_mode === 'horary' && q.horary_tables && (
                <section className="mb-10" data-testid="horary-planet-table">
                    <h2 className="font-serif text-2xl text-parchment mb-4">Planet significators</h2>
                    <PlanetTable rows={q.horary_tables.planets} />
                </section>
            )}

            {q.prediction_mode === 'horary' && q.ruling_planets && (
                <section className="mb-10">
                    <h2 className="font-serif text-2xl text-parchment mb-4">Ruling Planets at judgment</h2>
                    <RulingPlanetsCard rp={q.ruling_planets} />
                </section>
            )}

            <section className="mb-10">
                <h2 className="font-serif text-2xl text-parchment mb-4">Timing windows</h2>
                <TimingWindows windows={q.timing_windows} />
            </section>

            <section className="mb-10">
                <Card className="bg-surface border-marigold/15 rounded-sm p-6">
                    <div className="overline mb-4">Key planetary voices</div>
                    <div className="grid md:grid-cols-2 gap-6 text-sm">
                        <div>
                            <div className="text-emerald-400 font-medium mb-2">Favourable</div>
                            <div className="flex flex-wrap gap-2">
                                {(q.favorable_significators || []).length === 0 && <span className="text-slate-600">—</span>}
                                {q.favorable_significators?.map((n) => (
                                    <span key={n} className="px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-sm text-emerald-300">{n}</span>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="text-rose-400 font-medium mb-2">Cautionary</div>
                            <div className="flex flex-wrap gap-2">
                                {(q.adverse_significators || []).length === 0 && <span className="text-slate-600">—</span>}
                                {q.adverse_significators?.map((n) => (
                                    <span key={n} className="px-2.5 py-1 bg-rose-500/10 border border-rose-500/30 rounded-sm text-rose-300">{n}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </Card>
            </section>

            {q.prediction_mode !== 'horary' && (q.yogas?.length > 0 || q.yogakaraka) && (
                <section className="mb-10" data-testid="yogas-section">
                    <Card className="bg-surface border-marigold/15 rounded-sm p-6">
                        <div className="overline mb-4">Yogas &amp; lagna signatures</div>
                        {q.yogakaraka && (
                            <div className="mb-4 text-sm">
                                <span className="text-slate-400">Yogakaraka for your {q.chart.ascendant.sign} lagna: </span>
                                <span className="font-serif text-parchment text-lg">{q.yogakaraka}</span>
                            </div>
                        )}
                        {q.yogas?.length > 0 && (
                            <div className="space-y-3">
                                {q.yogas.map((y, i) => (
                                    <div key={i} data-testid={`yoga-${i}`}
                                        className={`p-4 rounded-sm border ${y.category === 'Kemadruma'
                                            ? 'bg-rose-500/5 border-rose-500/20'
                                            : 'bg-marigold/5 border-marigold/20'}`}>
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <span className="font-serif text-parchment text-xl">
                                                {y.name || y.category}
                                            </span>
                                            <span className="overline !text-[10px]">{y.category}</span>
                                            {y.planets && (
                                                <span className="text-xs text-copper">{y.planets.join(' · ')}</span>
                                            )}
                                            {y.planet && (
                                                <span className="text-xs text-copper">{y.planet} in {y.sign}, house {y.house}</span>
                                            )}
                                            {y.combo && (
                                                <span className="text-xs text-copper">{y.combo}</span>
                                            )}
                                        </div>
                                        {y.significance && (
                                            <p className="text-xs text-slate-400 mt-2">{y.significance}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                </section>
            )}

            <div className="text-center">
                <Link to="/app/ask">
                    <Button className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-none h-11 px-6">
                        Ask another question
                    </Button>
                </Link>
            </div>
            </div>{/* /cascade-3-extras */}
        </div>
    );
}

function FructificationCard({ f }) {
    if (!f) return null;
    const { status, message, windows = [] } = f;

    if (status === 'no_window') {
        const isNo = (f.verdict_label || '').toUpperCase() === 'NO';
        const Icon = isNo ? Ban : AlertCircle;
        const tone = isNo
            ? 'bg-rose-500/5 border-rose-500/20 text-rose-200'
            : 'bg-slate-500/5 border-slate-500/30 text-slate-300';
        return (
            <Card className={`rounded-sm p-6 border ${tone}`} data-testid="fructification-no-window">
                <div className="flex items-start gap-3">
                    <Icon size={18} className={isNo ? 'text-rose-400 mt-0.5' : 'text-slate-400 mt-0.5'} />
                    <div className="text-sm leading-relaxed">{message}</div>
                </div>
            </Card>
        );
    }

    const tentative = status === 'tentative';
    const wrapTone = tentative
        ? 'bg-amber-500/5 border-amber-500/20'
        : 'bg-emerald-500/5 border-emerald-500/20';
    const headTone = tentative ? 'text-amber-300' : 'text-emerald-300';

    return (
        <Card className={`rounded-sm p-6 border ${wrapTone}`} data-testid="fructification-windows">
            <div className="flex items-start gap-3 mb-5">
                <Hourglass size={18} className={headTone + ' mt-0.5'} />
                <div className={`text-sm leading-relaxed ${headTone}`}>{message}</div>
            </div>
            <ol className="space-y-4">
                {windows.map((w, i) => {
                    const start = new Date(w.start);
                    const end = new Date(w.end);
                    const fmt = (d) => d.toLocaleString(undefined, { month: 'short', year: 'numeric' });
                    const peak = w.peak_pratyantar;
                    const peakStart = peak ? new Date(peak.start) : null;
                    const peakEnd = peak ? new Date(peak.end) : null;
                    return (
                        <li key={i} className="flex flex-wrap items-start gap-3 border-t border-marigold/5 pt-4 first:border-0 first:pt-0" data-testid={`window-${i}`}>
                            <div className="w-10 h-10 rounded-sm bg-obsidian border border-marigold/20 flex items-center justify-center font-serif text-marigold text-lg flex-shrink-0">
                                {i + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 flex-wrap">
                                    <span className="font-serif text-xl text-parchment">{w.maha_lord} – {w.antar_lord}</span>
                                    <span className="text-xs text-slate-500">· {fmt(start)} → {fmt(end)}</span>
                                </div>
                                {peak && (
                                    <div className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-sm bg-marigold/10 border border-marigold/25">
                                        <span className="text-[10px] text-marigold uppercase tracking-widest font-semibold">Peak</span>
                                        <span className="text-xs text-parchment">{w.maha_lord} – {w.antar_lord} – {peak.lord}</span>
                                        <span className="text-xs text-slate-500">· {fmt(peakStart)} → {fmt(peakEnd)}</span>
                                    </div>
                                )}
                                <div className="text-xs text-slate-400 mt-2 leading-relaxed">{w.reason}</div>
                            </div>
                        </li>
                    );
                })}
            </ol>
        </Card>
    );
}

function RulingPlanetsCard({ rp }) {
    if (!rp) return null;
    const Pill = ({ children, accent = false, testId }) => (
        <span
            data-testid={testId}
            className={`inline-flex items-center px-3 py-1 rounded-sm text-xs font-medium border ${
                accent
                    ? 'bg-marigold/20 text-marigold border-marigold/40'
                    : 'bg-obsidian text-parchment border-marigold/15'
            }`}
        >
            {children}
        </span>
    );
    const Row = ({ label, sub, items }) => (
        <div className="grid grid-cols-1 md:grid-cols-[170px_1fr] gap-3 md:gap-6 py-3 border-t border-marigold/5 first:border-0">
            <div>
                <div className="overline">{label}</div>
                {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
            </div>
            <div className="flex flex-wrap gap-2">{items}</div>
        </div>
    );
    return (
        <Card className="rounded-sm bg-elevated border-marigold/10 p-6" data-testid="ruling-planets-card">
            <Row
                label="Day Lord"
                items={<Pill testId="rp-day-lord">{rp.day_lord}</Pill>}
            />
            <Row
                label="Moon"
                sub={`${rp.moon?.sign} · ${rp.moon?.nakshatra}`}
                items={<>
                    <Pill testId="rp-moon-sub">Sub: {rp.moon?.sub_lord}</Pill>
                    <Pill testId="rp-moon-star">Star: {rp.moon?.star_lord}</Pill>
                    <Pill testId="rp-moon-sign">Sign: {rp.moon?.sign_lord}</Pill>
                </>}
            />
            <Row
                label="Lagna (Asc.)"
                sub={`${rp.lagna?.sign} · ${rp.lagna?.nakshatra}`}
                items={<>
                    <Pill testId="rp-lagna-sub">Sub: {rp.lagna?.sub_lord}</Pill>
                    <Pill testId="rp-lagna-star">Star: {rp.lagna?.star_lord}</Pill>
                    <Pill testId="rp-lagna-sign">Sign: {rp.lagna?.sign_lord}</Pill>
                </>}
            />
            <div className="border-t border-marigold/5 pt-4 mt-3">
                <div className="overline mb-3">Active Ruling Planets</div>
                <div className="flex flex-wrap gap-2" data-testid="rp-unique">
                    {(rp.unique || []).map((p) => (
                        <Pill key={p} accent>{p}</Pill>
                    ))}
                </div>
                <div className="text-xs text-slate-500 mt-3 leading-relaxed">
                    Cast at <span className="text-slate-400">{rp.judgment_location}</span>. Per KP doctrine, an event materialises during a Dasha–Antar whose lords <em className="text-marigold/80 not-italic">also</em> feature in this Ruling Planet set.
                </div>
            </div>
        </Card>
    );
}



function HouseTable({ rows }) {
    if (!rows || rows.length === 0) return null;
    return (
        <Card className="rounded-sm bg-elevated border-marigold/10 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-left text-slate-500 text-xs uppercase tracking-wider border-b border-marigold/10">
                        <tr>
                            <th className="px-4 py-3">House</th>
                            <th className="px-4 py-3">Sign</th>
                            <th className="px-4 py-3">Cusp °</th>
                            <th className="px-4 py-3">Sign Lord</th>
                            <th className="px-4 py-3">Star Lord</th>
                            <th className="px-4 py-3">Sub Lord</th>
                            <th className="px-4 py-3">Occupants</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.house} className="border-b border-marigold/5 hover:bg-obsidian/40" data-testid={`house-row-${r.house}`}>
                                <td className="px-4 py-3 font-serif text-marigold">{r.house}</td>
                                <td className="px-4 py-3 text-parchment">{r.sign}</td>
                                <td className="px-4 py-3 text-slate-400">{r.degree}°</td>
                                <td className="px-4 py-3 text-slate-300">{r.sign_lord}</td>
                                <td className="px-4 py-3 text-slate-300">{r.star_lord}</td>
                                <td className="px-4 py-3 text-marigold font-medium">{r.sub_lord}</td>
                                <td className="px-4 py-3">
                                    {r.occupants?.length ? (
                                        <div className="flex flex-wrap gap-1">
                                            {r.occupants.map((o) => (
                                                <span key={o} className="px-2 py-0.5 bg-marigold/10 border border-marigold/20 rounded-sm text-[11px] text-parchment">
                                                    {o}
                                                </span>
                                            ))}
                                        </div>
                                    ) : <span className="text-slate-600 text-xs">—</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="px-4 py-3 border-t border-marigold/5 text-[11px] text-slate-500 leading-relaxed">
                <span className="text-marigold">Sub Lord</span> is the deciding factor in KP — it determines whether the house promises the matter at all.
            </div>
        </Card>
    );
}

function PlanetTable({ rows }) {
    if (!rows || rows.length === 0) return null;
    const renderHouses = (arr) => (
        arr && arr.length
            ? arr.join(', ')
            : <span className="text-slate-700">—</span>
    );
    return (
        <Card className="rounded-sm bg-elevated border-marigold/10 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-left text-slate-500 text-xs uppercase tracking-wider border-b border-marigold/10">
                        <tr>
                            <th className="px-3 py-3">Planet</th>
                            <th className="px-3 py-3">Position</th>
                            <th className="px-3 py-3">House</th>
                            <th className="px-3 py-3">Star Lord</th>
                            <th className="px-3 py-3">Sub Lord</th>
                            <th className="px-3 py-3" title="Star lord occupies these houses">A · star-lord occ.</th>
                            <th className="px-3 py-3" title="Planet occupies">B · occupies</th>
                            <th className="px-3 py-3" title="Star lord owns these houses">C · star-lord owns</th>
                            <th className="px-3 py-3" title="Planet is the lord of these houses">D · is lord of</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.name} className="border-b border-marigold/5 hover:bg-obsidian/40" data-testid={`planet-row-${r.name}`}>
                                <td className="px-3 py-3">
                                    <span className="font-serif text-parchment text-base">{r.name}</span>
                                    {r.retrograde && <span className="ml-1 text-rose-400 text-xs">℞</span>}
                                </td>
                                <td className="px-3 py-3 text-slate-300 whitespace-nowrap">
                                    {r.sign} {r.degree}°
                                    <div className="text-[10px] text-slate-500">{r.nakshatra}</div>
                                </td>
                                <td className="px-3 py-3 text-marigold font-medium">{r.house}</td>
                                <td className="px-3 py-3 text-slate-300">{r.star_lord}</td>
                                <td className="px-3 py-3 text-marigold">{r.sub_lord}</td>
                                <td className="px-3 py-3 text-emerald-300 text-xs">{renderHouses(r.level_a_houses)}</td>
                                <td className="px-3 py-3 text-emerald-200 text-xs">{renderHouses(r.level_b_houses)}</td>
                                <td className="px-3 py-3 text-amber-300 text-xs">{renderHouses(r.level_c_houses)}</td>
                                <td className="px-3 py-3 text-amber-200 text-xs">{renderHouses(r.level_d_houses)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="px-4 py-3 border-t border-marigold/5 text-[11px] text-slate-500 leading-relaxed">
                KP significator levels — <span className="text-emerald-200">B</span> &gt; <span className="text-emerald-300">A</span> &gt; <span className="text-amber-200">D</span> &gt; <span className="text-amber-300">C</span>. A planet physically <em>occupying</em> the house (B) is the strongest significator of that house. A planet in the star of an occupant (A) is second strongest. {/* KP1 confirmed order: Level B (occupant) is strongest, not Level A. */}
            </div>
        </Card>
    );
}


function RemediesCard({ question, onUnlocked }) {
    const { user, refresh } = useAuth();
    const [busy, setBusy] = useState(false);

    const cost = question.remedies_cost ?? 10;
    const unlocked = !!question.remedies_unlocked && !!question.remedies_text;
    const credits = user?.credits ?? 0;
    const canAfford = credits >= cost;

    const onUnlock = async () => {
        if (!canAfford) {
            toast.error(`You need ${cost} credits to reveal remedies. Top up first.`);
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/astrology/prediction/${question.id}/unlock-remedies`);
            onUnlocked({
                remedies_unlocked: true,
                remedies_text: data.remedies_text,
            });
            if (typeof refresh === 'function') refresh();
            toast.success(data.charged ? `Remedies unlocked — ${cost} credits used.` : 'Remedies revealed.');
        } catch (e) {
            toast.error(readErr(e, 'Could not unlock remedies — try again.'));
        } finally {
            setBusy(false);
        }
    };

    if (unlocked) {
        return (
            <Card className="bg-elevated border-marigold/15 rounded-sm p-8 mb-10" data-testid="remedies-unlocked">
                <div className="flex items-center justify-between mb-4">
                    <div className="overline flex items-center gap-2"><Gem size={12} /> Personalized remedies</div>
                    <span className="text-[10px] uppercase tracking-widest text-marigold">Unlocked</span>
                </div>
                <div className="prose prose-invert max-w-none font-serif text-base text-parchment leading-relaxed whitespace-pre-wrap">
                    {question.remedies_text}
                </div>
            </Card>
        );
    }

    return (
        <Card
            className="relative bg-elevated border-marigold/30 rounded-sm p-8 mb-10 overflow-hidden"
            data-testid="remedies-locked"
        >
            <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none opacity-30"
                style={{
                    backgroundImage:
                        'radial-gradient(circle at 30% 20%, rgba(212,168,83,0.2) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(196,149,106,0.15) 0%, transparent 50%)',
                }}
            />
            <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex items-start gap-4 flex-1">
                    <div className="w-11 h-11 rounded-sm bg-marigold/15 border border-marigold/40 flex items-center justify-center flex-shrink-0">
                        <Lock size={18} className="text-marigold" />
                    </div>
                    <div>
                        <div className="overline mb-2">Personalized remedies</div>
                        <h3 className="font-serif text-2xl text-parchment leading-tight">
                            Unlock the prescription crafted for this reading.
                        </h3>
                        <p className="text-sm text-slate-400 mt-2 leading-relaxed max-w-lg">
                            A personalised four-part remedy — mantra, charity, gemstone, daily ritual —
                            anchored to your current Mahadasha and the planets pressing this question.
                            One unlock, kept forever on this reading.
                        </p>
                    </div>
                </div>
                <div className="flex flex-col items-stretch md:items-end gap-2 md:w-56 flex-shrink-0">
                    <Button
                        onClick={onUnlock}
                        disabled={busy}
                        data-testid="remedies-unlock-btn"
                        className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-sm h-11 px-5 font-medium"
                    >
                        {busy ? 'Unlocking…' : (
                            <span className="flex items-center justify-center gap-2">
                                <Coins size={14} /> Reveal for {cost} credits
                            </span>
                        )}
                    </Button>
                    <span className={`text-xs text-center md:text-right ${canAfford ? 'text-slate-500' : 'text-rose-400'}`}>
                        {canAfford
                            ? `Your balance: ${credits} credits`
                            : `Need ${cost - credits} more credits`}
                    </span>
                    {!canAfford && (
                        <Link to="/app/credits" className="text-xs text-marigold hover:underline text-center md:text-right">
                            Top up →
                        </Link>
                    )}
                </div>
            </div>
        </Card>
    );
}
